import { memo, useCallback, useMemo } from "react";
import type { HttpTypes } from "@medusajs/types";
import { Select } from "@medusajs/ui";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { cn } from "@repo/design-system/utils";
import type { InlineAddressData } from "../validations";

const ADD_NEW_VALUE = "__add_new__";
const CUSTOM_ADDRESS_VALUE = "__custom__";

export interface AddressDropdownProps {
  /** Type of address selection (affects labels) */
  type: "shipping" | "billing";
  /** List of customer addresses */
  addresses: HttpTypes.StoreCustomerAddress[];
  /** Currently selected address ID (null if custom address or none selected) */
  selectedId: string | null;
  /** Custom/one-time address data (when not using saved address) */
  customAddress?: InlineAddressData;
  /** Callback when a saved address is selected */
  onSelect: (addressId: string) => void;
  /** Callback when custom address is re-selected */
  onSelectCustom?: () => void;
  /** Callback when "Add New Address" is selected */
  onAddNew: () => void;
  /** Callback when the selected saved address's edit button is clicked */
  onEdit?: (address: HttpTypes.StoreCustomerAddress) => void;
  /** Whether to disable selection */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Whether addresses are loading */
  isLoading?: boolean;
  /** Placeholder text when no address selected */
  placeholder?: string;
}

/**
 * Format a saved address for display in the dropdown
 */
function formatAddressLabel(address: HttpTypes.StoreCustomerAddress): string {
  const parts: string[] = [];

  // Address name or street address
  if (address.address_name) {
    parts.push(address.address_name);
  } else if (address.address_1) {
    parts.push(address.address_1);
  }

  // City
  if (address.city) {
    parts.push(address.city);
  }

  // State/Region and Postal Code
  const locationParts: string[] = [];
  if (address.province) {
    locationParts.push(address.province.toUpperCase());
  }
  if (address.postal_code) {
    locationParts.push(address.postal_code);
  }
  if (locationParts.length > 0) {
    parts.push(locationParts.join(" "));
  }

  return parts.join(", ");
}

/**
 * Format an inline/custom address for display
 */
function formatInlineAddressLabel(address: InlineAddressData): string {
  const parts: string[] = [];

  if (address.address_1) {
    parts.push(address.address_1);
  }

  if (address.city) {
    parts.push(address.city);
  }

  const locationParts: string[] = [];
  if (address.province) {
    locationParts.push(address.province.toUpperCase());
  }
  if (address.postal_code) {
    locationParts.push(address.postal_code);
  }
  if (locationParts.length > 0) {
    parts.push(locationParts.join(" "));
  }

  return parts.join(", ");
}

/**
 * Address dropdown selector component
 *
 * Features:
 * - Dropdown select for choosing from saved addresses
 * - Shows address name/street and city in dropdown
 * - "Add New Address" option at the bottom
 * - Loading skeleton state
 * - Error message display
 *
 * @example
 * ```tsx
 * <AddressDropdown
 *   type="shipping"
 *   addresses={addresses}
 *   selectedId={shippingAddressId}
 *   onSelect={setShippingAddressId}
 *   onAddNew={() => setDialogOpen(true)}
 *   error={errors.shippingAddressId?.message}
 * />
 * ```
 */
export const AddressDropdown = memo(function AddressDropdown({
  type,
  addresses,
  selectedId,
  customAddress,
  onSelect,
  onSelectCustom,
  onAddNew,
  onEdit,
  disabled = false,
  error,
  isLoading = false,
  placeholder = "Select an address"
}: AddressDropdownProps) {
  const selectId = `${type}-address-select`;

  // Determine if custom address is currently selected
  const isCustomSelected = !selectedId && customAddress !== undefined;

  // Find selected saved address for display
  const selectedSavedAddress = useMemo(
    () => addresses.find((a) => a.id === selectedId),
    [addresses, selectedId]
  );

  // Handle value change
  const handleValueChange = (value: string) => {
    // Ignore empty values - this can happen when Select initializes
    if (!value) {
      return;
    }

    if (value === ADD_NEW_VALUE) {
      // Don't actually select this value - just open the dialog
      // The select will revert to its previous value
      onAddNew();
      return;
    } else if (value === CUSTOM_ADDRESS_VALUE) {
      onSelectCustom?.();
    } else {
      onSelect(value);
    }
  };

  // Get the current select value - use undefined for no selection (not empty string)
  const selectValue = isCustomSelected
    ? CUSTOM_ADDRESS_VALUE
    : selectedId || undefined;

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (selectedSavedAddress) {
        onEdit?.(selectedSavedAddress);
      }
    },
    [onEdit, selectedSavedAddress]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-2" role="status" aria-label="Loading addresses">
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    );
  }

  // Empty state - no addresses and no custom address
  if (addresses.length === 0 && !customAddress) {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={onAddNew}
          disabled={disabled}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-left transition-colors",
            "hover:border-purple-400 hover:bg-purple-50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
            <Plus className="h-4 w-4 text-purple-600" aria-hidden="true" />
          </div>
          <span className="text-sm font-medium text-gray-700">
            Add your first address
          </span>
        </button>
        {error && (
          <p
            id={`${type}-address-error`}
            className="text-sm text-red-600"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  // Determine what to show in preview
  const showSavedAddressPreview = selectedSavedAddress !== undefined;
  const showCustomAddressPreview =
    isCustomSelected && customAddress !== undefined;

  // The trigger shows the whole selected address on one line (name, street,
  // locality) so no separate preview card is needed.
  const getDisplayValue = () => {
    if (showSavedAddressPreview && selectedSavedAddress) {
      const street = [
        selectedSavedAddress.address_1,
        selectedSavedAddress.address_2
      ]
        .filter(Boolean)
        .join(", ");
      const locality = [
        selectedSavedAddress.city,
        [
          selectedSavedAddress.province?.toUpperCase(),
          selectedSavedAddress.postal_code
        ]
          .filter(Boolean)
          .join(" ")
      ]
        .filter(Boolean)
        .join(", ");
      return (
        <span className="block truncate">
          {selectedSavedAddress.address_name && (
            <span className="font-medium text-gray-900">
              {selectedSavedAddress.address_name}
              {" · "}
            </span>
          )}
          {[street, locality].filter(Boolean).join(", ")}
        </span>
      );
    }
    if (showCustomAddressPreview && customAddress) {
      return (
        <span className="block truncate">
          {`${formatInlineAddressLabel(customAddress)} (One-time, not saved)`}
        </span>
      );
    }
    return placeholder;
  };

  return (
    <div className="space-y-2">
      {/* Single control: the trigger carries the full selected address, so
          no separate preview card is needed. */}
      <div className="flex items-center gap-2">
        <Select
          value={selectValue}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <Select.Trigger
            id={selectId}
            aria-label={`Select ${type} address`}
            aria-invalid={!!error}
            aria-describedby={error ? `${type}-address-error` : undefined}
            className={cn(
              // `min-w-0 flex-1`, not `w-full`: beside the edit button a long
              // address must shrink the trigger, not push the button off the card.
              // Radix ignores a className on `Select.Value`, so the value span
              // is reached from here; it truncates instead of pushing the
              // chevron out of the trigger.
              "min-w-0 flex-1 [&>span:first-child]:block [&>span:first-child]:min-w-0 [&>span:first-child]:truncate [&>span:first-child]:text-left",
              // The trigger renders the selected address as its label, and every
              // option in the list below is another address (ADR-0006).
              PII_MASK_CLASS,
              error && "border-red-500 focus:border-red-500"
            )}
          >
            <Select.Value placeholder={placeholder}>
              {getDisplayValue()}
            </Select.Value>
          </Select.Trigger>
          <Select.Content className={cn("z-[100]", PII_MASK_CLASS)}>
            {/* Custom address option (if one exists) */}
            {customAddress && (
              <>
                <Select.Item value={CUSTOM_ADDRESS_VALUE}>
                  {`${formatInlineAddressLabel(customAddress)} (One-time)`}
                </Select.Item>
                <div className="my-1 h-px bg-gray-200" />
              </>
            )}

            {/* Saved Addresses */}
            {addresses.map((address) => {
              const isDefault =
                (address.is_default_shipping && type === "shipping") ||
                (address.is_default_billing && type === "billing");
              const label = formatAddressLabel(address);
              return (
                <Select.Item key={address.id} value={address.id}>
                  {isDefault ? `${label} (Default)` : label}
                </Select.Item>
              );
            })}

            {/* Separator */}
            <div className="my-1 h-px bg-gray-200" />

            {/* Add New Option */}
            <Select.Item value={ADD_NEW_VALUE}>+ Add new address</Select.Item>
          </Select.Content>
        </Select>
        {showSavedAddressPreview && onEdit && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleEditClick}
            className="h-9 w-9 flex-shrink-0 p-0 text-gray-400 hover:text-gray-600"
            aria-label={`Edit ${selectedSavedAddress?.address_name || "address"}`}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p
          id={`${type}-address-error`}
          className="text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
});
